import { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Textarea } from "@/components/ui/Textarea";
import JsBarcode from "jsbarcode";
import { UAParser } from "ua-parser-js";
import { ToolPageLayout } from "@/components/ToolPageLayout";

export function BarcodeGenerator() {
  const [text, setText] = useState("123456789012");
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (canvasRef.current && text) {
      try {
        JsBarcode(canvasRef.current, text, { format: "CODE128", displayValue: true });
      } catch(e) {
        // invalid format
      }
    }
  }, [text]);

  return (
    <ToolPageLayout
      title="Barcode Generator Online"
      description="Create CODE128 barcodes instantly with our free online barcode generator."
      h1="Barcode Generator"
      category="Web Dev & Network"
      categoryPath="/web-dev-network"
      howToUse={[
        "Enter the text or numeric value you want to encode into a barcode.",
        "The CODE128 barcode image will be generated and updated in real-time."
      ]}
      faq={[
        { question: "What barcode format is used?", answer: "This generator uses the widely accepted CODE128 barcode standard." }
      ]}
    >
      <div className="space-y-6 max-w-sm mx-auto text-center">
        <div className="space-y-2 text-left">
          <Label>Barcode Value</Label>
          <Input value={text} onChange={e => setText(e.target.value)} />
        </div>
        <div className="p-4 bg-white border rounded-xl inline-block">
          <canvas ref={canvasRef} />
        </div>
      </div>
    </ToolPageLayout>
  );
}

export function MetaTagGenerator() {
  const [title, setTitle] = useState("My Website");
  const [desc, setDesc] = useState("A great website");
  const [img, setImg] = useState("https://example.com/image.png");

  const tags = `<!-- Primary Meta Tags -->
<title>${title}</title>
<meta name="title" content="${title}">
<meta name="description" content="${desc}">

<!-- Open Graph / Facebook -->
<meta property="og:type" content="website">
<meta property="og:title" content="${title}">
<meta property="og:description" content="${desc}">
<meta property="og:image" content="${img}">

<!-- Twitter -->
<meta property="twitter:card" content="summary_large_image">
<meta property="twitter:title" content="${title}">
<meta property="twitter:description" content="${desc}">
<meta property="twitter:image" content="${img}">`;

  return (
    <ToolPageLayout
      title="Meta Tag Generator"
      description="Generate HTML meta tags for SEO, Open Graph (Facebook), and Twitter cards instantly."
      h1="Meta Tag Generator"
      category="Web Dev & Network"
      categoryPath="/web-dev-network"
      howToUse={[
        "Fill in the page title, description, and social image URL.",
        "Copy the generated HTML tags and paste them into the <head> section of your website."
      ]}
      faq={[
        { question: "Why are meta tags important?", answer: "Meta tags provide search engines and social networks with crucial details about your webpage." }
      ]}
    >
      <div className="grid md:grid-cols-2 gap-8">
        <div className="space-y-4">
          <div className="space-y-2"><Label>Title</Label><Input value={title} onChange={e => setTitle(e.target.value)} /></div>
          <div className="space-y-2"><Label>Description</Label><Textarea value={desc} onChange={e => setDesc(e.target.value)} /></div>
          <div className="space-y-2"><Label>Image URL</Label><Input value={img} onChange={e => setImg(e.target.value)} /></div>
        </div>
        <div>
          <Label className="mb-2 block">Generated HTML</Label>
          <Textarea value={tags} readOnly className="min-h-[300px] font-mono text-sm bg-slate-50" />
        </div>
      </div>
    </ToolPageLayout>
  );
}

export function UserAgentParser() {
  const [uaStr, setUaStr] = useState(navigator.userAgent);
  const parser = new UAParser(uaStr);
  const result = parser.getResult();

  return (
    <ToolPageLayout
      title="User-Agent Parser Online"
      description="Parse, decode, and analyze any browser user-agent string instantly with our free online user-agent parser."
      h1="User-Agent Parser"
      category="Network Tools"
      categoryPath="/network-tools"
      howToUse={[
        "By default, your current browser user-agent string is loaded.",
        "Modify the string to analyze a different client device, browser, or OS."
      ]}
      faq={[
        { question: "What is a user-agent string?", answer: "A user-agent is a line of text that your browser sends to web servers to identify its version, engine, and operating system." }
      ]}
    >
      <div className="space-y-6">
        <div className="space-y-2">
          <Label>User Agent String</Label>
          <Textarea value={uaStr} onChange={e => setUaStr(e.target.value)} />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-4 border rounded-lg bg-slate-50"><div className="text-xs text-slate-500 uppercase">Browser</div><div className="font-bold">{result.browser.name} {result.browser.version}</div></div>
          <div className="p-4 border rounded-lg bg-slate-50"><div className="text-xs text-slate-500 uppercase">OS</div><div className="font-bold">{result.os.name} {result.os.version}</div></div>
          <div className="p-4 border rounded-lg bg-slate-50"><div className="text-xs text-slate-500 uppercase">Device</div><div className="font-bold">{result.device.vendor || "Unknown"} {result.device.model}</div></div>
          <div className="p-4 border rounded-lg bg-slate-50"><div className="text-xs text-slate-500 uppercase">Engine</div><div className="font-bold">{result.engine.name}</div></div>
        </div>
      </div>
    </ToolPageLayout>
  );
}
